import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FileText, PaperPlaneTilt, Check, X, Eye, Download,
  MagnifyingGlass, ShieldCheck, WarningCircle, Clock, ArrowRight, Phone as PhoneIcon
} from "@phosphor-icons/react";

const STATUS_STYLES = {
  draft: { bg: "bg-gray-100", text: "text-gray-700", label: "Draft" },
  sent: { bg: "bg-blue-100", text: "text-blue-700", label: "Sent" },
  awaiting_signature: { bg: "bg-amber-100", text: "text-amber-700", label: "Awaiting Signature" },
  signed: { bg: "bg-green-100", text: "text-green-700", label: "Signed" },
  expired: { bg: "bg-red-100", text: "text-red-700", label: "Expired" },
  cancelled: { bg: "bg-gray-100", text: "text-gray-500", label: "Cancelled" },
};

export default function ContractsPage() {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => { loadContracts(); }, [filterStatus]);

  const loadContracts = async () => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filterStatus) params.set("status", filterStatus);
      const { data } = await api.get(`/contracts?${params}`);
      setContracts(data.items || []);
    } catch { toast.error("Failed to load contracts"); }
    finally { setLoading(false); }
  };

  const handleSend = async (contractId) => {
    try {
      const { data } = await api.post(`/contracts/${contractId}/send`);
      toast.success("Contract sent");
      loadContracts();
      if (selected?.id === contractId) {
        const updated = await api.get(`/contracts/${contractId}`);
        setSelected(updated.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
  };

  const handleCancel = async (contractId) => {
    if (!window.confirm("Cancel this contract?")) return;
    try {
      await api.put(`/contracts/${contractId}/cancel`);
      toast.success("Contract cancelled");
      loadContracts();
      setSelected(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleOverride = async (contractId) => {
    if (!window.confirm("Allow vacancy to be worked without a signed contract?")) return;
    try {
      await api.put(`/contracts/${contractId}/override`);
      toast.success("Admin override applied");
      loadContracts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDownloadPdf = async (contractId) => {
    try {
      const response = await api.get(`/contracts/${contractId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `contract-${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const filtered = contracts.filter(c =>
    !search || c.vacancy_title?.toLowerCase().includes(search.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contract_number?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: contracts.length,
    signed: contracts.filter(c => c.status === "signed").length,
    pending: contracts.filter(c => ["draft", "sent", "awaiting_signature"].includes(c.status)).length,
  };

  if (selected) {
    const s = STATUS_STYLES[selected.status] || STATUS_STYLES.draft;
    return (
      <div data-testid="contract-detail" className="space-y-4">
        <button onClick={() => setSelected(null)} className="text-xs text-[#1E3A8A] hover:underline" data-testid="back-to-contracts">&larr; All Contracts</button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#0F172A]">{selected.vacancy_title}</h1>
            <p className="text-sm text-[#6B7280]">{selected.contract_number} · {selected.company_name}</p>
          </div>
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Fee", val: `${selected.fee_percentage}%`, color: "#C5A059" },
            { label: "Fee Amount", val: `\u00a3${selected.fee_amount?.toLocaleString()}`, color: "#1E3A8A" },
            { label: "Total (inc. VAT)", val: `\u00a3${selected.total_amount?.toLocaleString()}`, color: "#0F172A" },
            { label: "Guarantee", val: selected.guarantee_days > 0 ? `${selected.guarantee_days} days` : "None", color: "#10B981" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-lg border p-3">
              <p className="text-[10px] text-[#6B7280] uppercase font-medium">{s.label}</p>
              <p className="text-sm font-bold mt-0.5" style={{ color: s.color }}>{s.val}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Contract Details</h3>
          <div className="space-y-2 text-sm">
            {[
              ["Contact Person", selected.contact_person],
              ["Contact Email", selected.contact_email],
              ["Salary", `\u00a3${selected.placement_salary?.toLocaleString()}`],
              ["Payment Terms", selected.payment_terms],
              ["Created By", selected.created_by_name],
              ["Created", selected.created_at ? new Date(selected.created_at).toLocaleString() : ""],
              ["Sent", selected.sent_at ? new Date(selected.sent_at).toLocaleString() : "Not sent"],
              ["Signed", selected.signed_at ? `${new Date(selected.signed_at).toLocaleString()} by ${selected.signed_by_name}` : "Not signed"],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex justify-between py-1.5 border-b border-[#F3F4F6]">
                <span className="text-[#6B7280]">{k}</span>
                <span className="font-medium text-[#0F172A] text-right max-w-[60%]">{v}</span>
              </div>
            ))}
          </div>
          {selected.special_terms && (
            <div>
              <p className="text-xs font-semibold text-[#6B7280] uppercase mb-1">Special Terms</p>
              <p className="text-sm text-[#0F172A] bg-[#F9FAFB] p-3 rounded">{selected.special_terms}</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(selected.id)} className="gap-1" data-testid="download-contract-pdf"><Download size={14} /> Download PDF</Button>
          {["draft", "sent"].includes(selected.status) && (
            <Button size="sm" onClick={() => handleSend(selected.id)} className="bg-[#1E3A8A] gap-1" data-testid="send-contract-btn"><PaperPlaneTilt size={14} /> {selected.status === "draft" ? "Send to Client" : "Resend"}</Button>
          )}
          {user?.role === "admin" && !["signed", "cancelled"].includes(selected.status) && (
            <>
              <Button size="sm" variant="outline" onClick={() => handleOverride(selected.id)} className="gap-1 text-amber-600 border-amber-300" data-testid="override-contract-btn"><ShieldCheck size={14} /> Admin Override</Button>
              <Button size="sm" variant="outline" onClick={() => handleCancel(selected.id)} className="gap-1 text-red-500 border-red-300" data-testid="cancel-contract-btn"><X size={14} /> Cancel</Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contracts-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Contracts & Guarantees</h1>
          <p className="text-sm text-[#6B7280] mt-1">Recruitment Services Agreements and replacement guarantees</p>
          <a href="tel:02045850050" className="text-xs text-[#6B7280] flex items-center gap-1 mt-1 hover:text-[#0F172A]"><PhoneIcon size={11} className="text-[#C5A059]" /> 020 4585 0050</a>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Total Contracts", val: stats.total, icon: FileText, color: "#0F172A" },
          { label: "Signed", val: stats.signed, icon: Check, color: "#10B981" },
          { label: "Pending", val: stats.pending, icon: Clock, color: "#F59E0B" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg border p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + "12" }}>
              <s.icon size={20} style={{ color: s.color }} weight="duotone" />
            </div>
            <div>
              <p className="text-[10px] text-[#6B7280] uppercase font-medium">{s.label}</p>
              <p className="text-lg font-bold" style={{ color: s.color }}>{s.val}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        <Input placeholder="Search contracts..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" data-testid="search-contracts-input" />
        <select className="border rounded-lg px-3 py-2 text-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="filter-contract-status">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="signed">Signed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 skeleton-shimmer rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <FileText size={48} className="text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-[#6B7280]">No contracts found</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Generate contracts from the Vacancy Workspace</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => {
            const st = STATUS_STYLES[c.status] || STATUS_STYLES.draft;
            return (
              <button key={c.id} onClick={() => setSelected(c)} className="w-full bg-white rounded-lg border border-[#E5E7EB] p-4 text-left hover:shadow-md transition-shadow" data-testid={`contract-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-[#0F172A] truncate">{c.vacancy_title}</h3>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${st.bg} ${st.text}`}>{st.label}</span>
                    </div>
                    <p className="text-xs text-[#6B7280] mt-0.5">{c.contract_number} · {c.company_name}</p>
                    <div className="flex gap-3 mt-1.5">
                      <span className="text-[10px] text-[#C5A059] font-medium">{c.fee_percentage}% fee</span>
                      <span className="text-[10px] text-[#1E3A8A]">{"\u00a3"}{c.fee_amount?.toLocaleString()}</span>
                      <span className="text-[10px] text-[#10B981]">{c.guarantee_days > 0 ? `${c.guarantee_days}d guarantee` : "No guarantee"}</span>
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-[#C5A059] flex-shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
